import { FC, memo } from 'react'

import { Form } from 'brief-form'
import { states } from 'commons/types/dictionaries'
import { LeaveConfirmation, Toggle } from 'ui/components'
import { DatePicker } from 'ui/components/DatePicker'
import { Hr } from 'ui/components/Hr'
import { PercentInput, TextInput, TextareaInput, USPhoneInput } from 'ui/components/InputV2'
import { Select } from 'ui/components/Select'
import { Col, Col2x, FormRow } from 'ui/themes/globalStyles'

import { ManageLicenseFormProps } from './ManageLicenseFormProps'

import * as SC from './styles'

export const ManageLicenseForm: FC<ManageLicenseFormProps> = memo((props) => {
  const { config, Field, isDirty, isLeaveConfirmationNeeded, data } = props
  const { companyName, licenseSubtypeOptions, licenseTypeOptions, licensePOSTypeOptions } = data

  return (
    <SC.Box>
      <LeaveConfirmation preventLeaving={isLeaveConfirmationNeeded && isDirty}>
        <Form config={config}>
          <SC.FieldSetStyled legend="License Details">
            <FormRow>
              <Col>
                <Field
                  required
                  name="name"
                  label="Name"
                  input={TextInput}
                  inputProps={{
                    'data-qa': 'License Name',
                    maxLength: 100
                  }}
                  validator={(v) => (v ? undefined : 'Name is required')}
                />
              </Col>
              <Col>
                <Field
                  required
                  name="licenseNumber"
                  label="License Number"
                  input={TextInput}
                  inputProps={{
                    'data-qa': 'License Number',
                    maxLength: 100
                  }}
                  validator={(v) => (v ? undefined : 'License Number is required')}
                />
              </Col>
            </FormRow>
            <FormRow>
              <Col>
                <Field
                  name="companyId"
                  label="Customer"
                  input={Select}
                  inputProps={{
                    data: [{ label: companyName, value: config.value.companyId }],
                    'options-data-qa': 'opt',
                    disabled: true
                  }}
                />
              </Col>
              <Col>
                <Field
                  required
                  name="issueDate"
                  label="Issue Date"
                  input={DatePicker}
                  validator={(v) => (v ? undefined : 'Issue date is required')}
                  inputProps={{}}
                />
              </Col>
            </FormRow>
            <FormRow>
              <Col>
                <Field
                  name="active"
                  label="Active"
                  input={Toggle}
                  inputProps={{
                    'data-qa': 'Active',
                    label: ''
                  }}
                />
              </Col>
              <Col>
                <SC.VisibilityBox $visible={!config.value.active}>
                  <Field
                    required={!config.value.active}
                    name="nullificationDate"
                    label="Nullification Date"
                    input={DatePicker}
                    validator={(v, f) => (!v && !f.active ? 'Nullification date is required' : undefined)}
                    inputProps={{}}
                  />
                </SC.VisibilityBox>
              </Col>
            </FormRow>
            <FormRow>
              <Col>
                <Field
                  required
                  name="type"
                  input={Select}
                  inputProps={{
                    'data-qa': 'License Type',
                    'options-data-qa': 'License Type',
                    data: licenseTypeOptions
                  }}
                  label="Type"
                  validator={(v) => (v ? undefined : 'Type is required')}
                />
              </Col>
              <Col>
                <Field
                  required
                  name="subtype"
                  input={Select}
                  inputProps={{
                    'data-qa': 'License Subtype',
                    'options-data-qa': 'License Subtype',
                    data: licenseSubtypeOptions
                  }}
                  label="Subtype"
                  validator={(v) => (v ? undefined : 'License Subtype is required')}
                />
              </Col>
            </FormRow>
            <FormRow>
              <Col>
                <Field
                  name="phone"
                  label="Phone"
                  input={USPhoneInput}
                  inputProps={{
                    'data-qa': 'Phone',
                    maxLength: 25
                  }}
                />
              </Col>
              <Col>
                <Field
                  required
                  name="posType"
                  input={Select}
                  inputProps={{
                    'data-qa': 'POS Type',
                    'options-data-qa': 'POS Type',
                    data: licensePOSTypeOptions
                  }}
                  label="POS Type"
                  validator={(v) => (v ? undefined : 'POS Type is required')}
                />
                <SC.VisibilityBox $visible={config.value.posType == 'other'}>
                  <Field
                    required={config.value.posType == 'other'}
                    name="posTypeOther"
                    input={TextInput}
                    inputProps={{
                      'data-qa': 'POS Type Other',
                      maxLength: 100
                    }}
                    label="POS Type Other"
                    validator={(v, f) => (!v && f.posType == 'other' ? 'POS Type Other is required' : undefined)}
                  />
                </SC.VisibilityBox>
              </Col>
            </FormRow>
            <FormRow>
              <FormRow>
                <Col2x>
                  <Field
                    name="internalNotes"
                    label="Notes"
                    input={TextareaInput}
                    inputProps={{
                      'data-qa': 'Notes'
                    }}
                  />
                </Col2x>
              </FormRow>
            </FormRow>
            <Hr />
          </SC.FieldSetStyled>
          <SC.FieldSetStyled legend="Tax rates for License">
            <FormRow>
              <Col>
                <Field
                  name="cityTax"
                  label="City Tax"
                  input={PercentInput}
                  inputProps={{
                    'data-qa': 'City Tax',
                    scale: 4
                  }}
                />
              </Col>
              <Col>
                <Field
                  name="countyTax"
                  label="County Tax"
                  input={PercentInput}
                  inputProps={{
                    'data-qa': 'County Tax',
                    scale: 4
                  }}
                />
              </Col>
            </FormRow>
            <FormRow>
              <Col>
                <Field
                  name="mjRetailTax"
                  label="MJ Retail Tax"
                  input={PercentInput}
                  inputProps={{
                    'data-qa': 'MJ Retail Tax',
                    scale: 4
                  }}
                />
              </Col>
              <Col>
                <Field
                  name="stateTax"
                  label="State Tax"
                  input={PercentInput}
                  inputProps={{
                    'data-qa': 'State Tax',
                    scale: 4
                  }}
                />
              </Col>
            </FormRow>
            <FormRow>
              <Col>
                <Field
                  name="specialTax"
                  label="Special Tax"
                  input={PercentInput}
                  inputProps={{
                    'data-qa': 'Special Tax',
                    scale: 4
                  }}
                />
              </Col>
            </FormRow>
            <Hr />
          </SC.FieldSetStyled>
          <SC.FieldSetStyled legend="Address Information">
            <FormRow>
              <Col>
                <Field
                  name="streetAddress"
                  label="Street Address"
                  input={TextInput}
                  inputProps={{
                    'data-qa': 'Street Address',
                    maxLength: 100
                  }}
                />
              </Col>
              <Col>
                <Field
                  required
                  name="state"
                  label="State"
                  input={Select}
                  inputProps={{
                    'data-qa': 'State',
                    'options-data-qa': 'State',
                    data: states
                  }}
                  validator={(v) => (v ? undefined : 'State is required')}
                />
              </Col>
            </FormRow>
            <FormRow>
              <Col>
                <Field
                  name="postalCode"
                  label="Zip Code"
                  input={TextInput}
                  inputProps={{
                    'data-qa': 'Zip Code',
                    maxLength: 25
                  }}
                />
              </Col>
              <Col>
                <Field
                  name="city"
                  label="City"
                  input={TextInput}
                  inputProps={{
                    'data-qa': 'City',
                    maxLength: 100
                  }}
                />
              </Col>
            </FormRow>
          </SC.FieldSetStyled>
        </Form>
      </LeaveConfirmation>
    </SC.Box>
  )
})
